import { useEffect, useState, type ReactNode } from 'react';
import { useDarkMode } from '../hooks/useDarkMode';
import { AdminRoleContext, type AdminRole } from '../context/AdminRoleContext';

type GateState = 'checking' | 'authed' | 'unauthed' | 'unconfigured' | 'error';

export default function AdminGate({
  children,
  requireFullAdmin = false,
}: {
  children: ReactNode;
  requireFullAdmin?: boolean;
}) {
  const isDark = useDarkMode();
  const [state, setState] = useState<GateState>('checking');
  const [role, setRole] = useState<AdminRole>(null);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const checkStatus = async () => {
    try {
      const res = await fetch('/api/admin-login', { credentials: 'same-origin' });
      if (res.status === 200) {
        const data = await res.json().catch(() => ({}));
        setRole(((data?.role as AdminRole) || 'admin'));
        setState('authed');
      } else if (res.status === 503) setState('unconfigured');
      else if (res.status === 401) setState('unauthed');
      else setState('error');
    } catch {
      setState('error');
    }
  };

  useEffect(() => {
    checkStatus();
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch('/api/admin-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ password }),
      });
      if (res.status === 200) {
        const data = await res.json().catch(() => ({}));
        setRole(((data?.role as AdminRole) || 'admin'));
        setState('authed');
        setPassword('');
      } else if (res.status === 503) {
        setState('unconfigured');
      } else {
        setError('Senha incorreta.');
      }
    } catch {
      setError('Falha de conexão. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  if (state === 'authed') {
    if (requireFullAdmin && role !== 'admin') {
      return (
        <CenterCard
          title="Sem permissão"
          message="Esta área é restrita ao administrador. Seu acesso é de visualização — Dashboard, Funil, Campanhas e Conversas."
          action={{ label: 'Voltar ao painel', onClick: () => { window.location.href = '/admin'; } }}
        />
      );
    }
    return <AdminRoleContext.Provider value={role}>{children}</AdminRoleContext.Provider>;
  }

  if (state === 'checking') return <CenterMessage text="Verificando acesso…" />;

  if (state === 'unconfigured')
    return (
      <CenterCard
        title="Acesso administrativo não configurado"
        message="Defina as variáveis ADMIN_PASSWORD e ADMIN_SECRET no Vercel e refaça o deploy."
      />
    );

  if (state === 'error')
    return (
      <CenterCard
        title="Não foi possível verificar o acesso"
        message="Recarregue a página. Se o problema persistir, verifique sua conexão."
        action={{ label: 'Tentar novamente', onClick: checkStatus }}
      />
    );

  return (
    <div
      className="admin-shell"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'DM Sans', sans-serif",
        padding: 16,
      }}
    >
      <form
        onSubmit={submit}
        className="admin-glass"
        style={{
          background: 'var(--surface-white)',
          borderRadius: 22,
          padding: 32,
          width: '100%',
          maxWidth: 380,
        }}
      >
        <img src={isDark ? '/logo-dark.png' : '/logo-light.png'} alt="Inspira" style={{ height: 36, marginBottom: 18 }} />
        <h1
          style={{
            margin: '0 0 6px',
            fontSize: 20,
            fontWeight: 800,
            color: 'var(--heading-ink)',
            letterSpacing: '-0.02em',
          }}
        >
          Área administrativa
        </h1>
        <p style={{ margin: '0 0 18px', fontSize: 13, color: 'var(--text-muted)' }}>
          Informe a senha para continuar.
        </p>
        <input
          type="password"
          autoFocus
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (error) setError('');
          }}
          placeholder="Senha"
          autoComplete="current-password"
          style={{
            width: '100%',
            padding: '12px 16px',
            borderRadius: 12,
            border: '1px solid var(--border-soft)',
            background: 'var(--cream)',
            color: 'var(--text-body)',
            fontSize: 15,
            outline: 'none',
            boxSizing: 'border-box',
            fontFamily: 'inherit',
          }}
        />
        {error && (
          <p style={{ margin: '10px 0 0', fontSize: 13, color: '#a93232' }}>{error}</p>
        )}
        <button
          type="submit"
          disabled={submitting || !password}
          style={{
            marginTop: 18,
            width: '100%',
            padding: '12px 0',
            borderRadius: 999,
            border: 'none',
            background: submitting || !password ? 'var(--gold-light)' : 'var(--gold)',
            color: 'var(--gold-ink)',
            fontWeight: 700,
            fontSize: 15,
            cursor: submitting || !password ? 'default' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {submitting ? 'Validando…' : 'Entrar'}
        </button>
      </form>
    </div>
  );
}

function CenterMessage({ text }: { text: string }) {
  return (
    <div
      className="admin-shell"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--text-muted)',
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 14,
      }}
    >
      {text}
    </div>
  );
}

function CenterCard({
  title,
  message,
  action,
}: {
  title: string;
  message: string;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div
      className="admin-shell"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <div
        className="admin-glass"
        style={{
          maxWidth: 420,
          padding: 28,
          borderRadius: 22,
          background: 'var(--surface-white)',
          textAlign: 'center',
        }}
      >
        <h1 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 800, color: 'var(--heading-ink)' }}>
          {title}
        </h1>
        <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)' }}>{message}</p>
        {action && (
          <button
            onClick={action.onClick}
            style={{
              marginTop: 18,
              padding: '10px 20px',
              borderRadius: 999,
              border: 'none',
              background: 'var(--gold)',
              color: 'var(--gold-ink)',
              fontFamily: 'inherit',
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {action.label}
          </button>
        )}
      </div>
    </div>
  );
}
